import type {
  Account,
  Budget,
  CalendarEvent,
  CurrencyRate,
  FinanceCategory,
  DailyPlan,
  DailyReview,
  DeletedItem,
  DirtyItem,
  Goal,
  Habit,
  InboxItem,
  InboxStatus,
  KeyResult,
  LifeOsCollectionName,
  LifeOsState,
  Note,
  NoteAttachment,
  Priority,
  Project,
  ProjectStatus,
  ProjectTemplate,
  RecurringRule,
  Task,
  TaskSubtask,
  TaskStatus,
  Transaction,
  TransactionType,
  WeeklyReview,
} from "../types";
import { todayIso } from "./date";
import { createDefaultState, createEmptyState } from "./default-state-seed";
import {
  accountTypeValue,
  estimateMinutesValue,
  goalCurrentValue,
  habitTargetPerWeekValue,
  nonNegativeNumberValue,
  numberValue,
  optionalProgressMode,
  positiveNumberValue,
  progressValue,
} from "./domainValues";

// Seed states live in default-state-seed.ts; re-exported so existing import
// sites (`from "./storage"`) keep working unchanged.
export { DEFAULT_ACCOUNT_ID, createDefaultState, createEmptyState } from "./default-state-seed";

const STORAGE_KEY = "personal-life-os-state:v1";
const INDEXED_STATE_REFERENCE = "personal-life-os:indexeddb";
export const INDEXED_STATE_PLACEHOLDER_UPDATED_AT = "1970-01-01T00:00:00.000Z";
const collectionNames: LifeOsCollectionName[] = [
  "tasks",
  "projects",
  "goals",
  "habits",
  "notes",
  "transactions",
  "budgets",
  "inbox",
  "events",
  "recurring",
  "accounts",
  "dailyPlans",
  "dailyReviews",
  "weeklyReviews",
  "templates",
  "currencyRates",
  "financeCategories",
];
const taskStatuses: TaskStatus[] = ["todo", "doing", "done"];
const priorities: Priority[] = ["low", "medium", "high"];
const projectStatuses: ProjectStatus[] = ["active", "paused", "done"];
const transactionTypes: TransactionType[] = ["income", "expense"];

export function makeId(prefix: string) {
  return `${prefix}_${crypto.randomUUID()}`;
}

export function createLocalStateStorageKey(scope = "local") {
  return scope === "local" ? STORAGE_KEY : `${STORAGE_KEY}:${scope}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function optionalString(value: unknown) {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function optionalDate(value: unknown) {
  const text = stringValue(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : undefined;
}

function optionalIso(value: unknown) {
  const text = stringValue(value);
  return text && !Number.isNaN(Date.parse(text)) ? text : undefined;
}

function optionalTime(value: unknown) {
  const text = stringValue(value);
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text) ? text : undefined;
}

function optionalNumber(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function optionalStringArray(value: unknown) {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value.map((entry) => String(entry).trim()).filter(Boolean);
  return items.length ? items : undefined;
}

function dateStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) => String(entry)).filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry));
}

function idStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0) : [];
}

/** Build an object containing only the keys whose value is not undefined (keeps JSON payloads clean). */
function withDefined<T extends object>(base: T, extras: Record<string, unknown>): T {
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [key, value] of Object.entries(extras)) {
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as T;
}

function dateValue(value: unknown, fallback: string) {
  const text = stringValue(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : fallback;
}

function timeValue(value: unknown, fallback: string) {
  const text = stringValue(value);
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text) ? text : fallback;
}

function isoValue(value: unknown, fallback: string) {
  const text = stringValue(value);
  return Number.isNaN(Date.parse(text)) ? fallback : text;
}

function enumValue<T extends string>(value: unknown, allowed: T[], fallback: T) {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function normalizeRecords(value: unknown, fallback: unknown[]) {
  return (Array.isArray(value) ? value : fallback).filter(isRecord);
}

function normalizeSubtasks(value: unknown, fallback: TaskSubtask[] = []): TaskSubtask[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value.filter(isRecord).map((item, index) => ({
    id: stringValue(item.id).trim() || `subtask_${index}`,
    title: stringValue(item.title).trim() || `Việc con ${index + 1}`,
    done: typeof item.done === "boolean" ? item.done : false,
  }));
}

/**
 * Sanitize one attachment/receipt ref. Carries inline `dataUrl` (legacy/local) and/or
 * the cloud Storage `path`; intentionally DROPS the transient signed `url` so it never
 * round-trips into persisted/synced state.
 */
function normalizeAttachmentRef(item: Record<string, unknown>, index = 0): NoteAttachment {
  // Stored-XSS hardening: only a real data: URI may survive as inline payload.
  // Junk schemes (http:, javascript:, …) are dropped even when a cloud `path`
  // coexists, so they can never reach a renderer later.
  const dataUrl = stringValue(item.dataUrl);
  return withDefined<NoteAttachment>(
    {
      id: stringValue(item.id).trim() || `attachment_${index}`,
      name: stringValue(item.name).trim() || `Attachment ${index + 1}`,
      size: Math.max(0, Number(item.size) || 0),
      type: stringValue(item.type).trim() || "application/octet-stream",
    },
    {
      dataUrl: dataUrl.startsWith("data:") ? dataUrl : undefined,
      path: optionalString(item.path),
      uploadedAt: optionalIso(item.uploadedAt),
    },
  );
}

/** True when a ref points to real data: inline base64 OR a cloud Storage path. */
function hasAttachmentPayload(ref: NoteAttachment): boolean {
  return Boolean(ref.dataUrl?.startsWith("data:")) || Boolean(ref.path);
}

function normalizeAttachments(value: unknown, fallback: NoteAttachment[] = []): NoteAttachment[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value
    .filter(isRecord)
    .map((item, index) => normalizeAttachmentRef(item, index))
    .filter(hasAttachmentPayload);
}

/** Single transaction receipt ref, or undefined when absent/empty. */
function normalizeReceipt(value: unknown): NoteAttachment | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const ref = normalizeAttachmentRef(value);
  return hasAttachmentPayload(ref) ? ref : undefined;
}

function readStorageItem(storageKey: string) {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeStorageItem(storageKey: string, value: string): boolean {
  try {
    localStorage.setItem(storageKey, value);
    return true;
  } catch {
    // Keep the app usable in private mode, quota errors, or restricted WebViews —
    // but report failure so callers can warn the user (unsaved changes).
    return false;
  }
}

function isIndexedStateReference(value: unknown): value is { storage: typeof INDEXED_STATE_REFERENCE; updatedAt?: string } {
  return isRecord(value) && value.storage === INDEXED_STATE_REFERENCE;
}

export function isIndexedLocalStateReference(raw: string | null) {
  if (!raw) {
    return false;
  }

  try {
    return isIndexedStateReference(JSON.parse(raw));
  } catch {
    return false;
  }
}

export function saveIndexedLocalStateReference(updatedAt: string, storageKey = createLocalStateStorageKey()): boolean {
  return writeStorageItem(
    storageKey,
    JSON.stringify({
      storage: INDEXED_STATE_REFERENCE,
      updatedAt,
    }),
  );
}

function normalizeTask(item: Record<string, unknown>, fallback: Task): Task {
  return withDefined<Task>(
    {
      id: stringValue(item.id, fallback.id),
      title: stringValue(item.title, fallback.title).trim() || fallback.title,
      dueDate: dateValue(item.dueDate, fallback.dueDate),
      status: enumValue(item.status, taskStatuses, fallback.status),
      priority: enumValue(item.priority, priorities, fallback.priority),
      projectId: optionalString(item.projectId),
      goalId: optionalString(item.goalId),
      estimateMinutes: estimateMinutesValue(item.estimateMinutes, fallback.estimateMinutes),
      subtasks: normalizeSubtasks(item.subtasks, fallback.subtasks),
    },
    {
      description: optionalString(item.description),
      tags: optionalStringArray(item.tags),
      deferDate: optionalDate(item.deferDate),
      createdAt: optionalIso(item.createdAt),
      completedAt: optionalIso(item.completedAt),
      recurrence: optionalString(item.recurrence),
      recurrenceParentId: optionalString(item.recurrenceParentId),
      recurrenceKey: optionalString(item.recurrenceKey),
      recurrenceExdates: optionalStringArray(item.recurrenceExdates),
      // Focus-session time tracking: clamp to ≥ 0; absent/invalid → undefined (no forced 0).
      loggedMinutes:
        optionalNumber(item.loggedMinutes) !== undefined
          ? Math.max(0, Math.round(Number(item.loggedMinutes)))
          : undefined,
    },
  );
}

function normalizeProject(item: Record<string, unknown>, fallback: Project): Project {
  return withDefined<Project>(
    {
      id: stringValue(item.id, fallback.id),
      name: stringValue(item.name, fallback.name).trim() || fallback.name,
      area: stringValue(item.area, fallback.area).trim() || fallback.area,
      status: enumValue(item.status, projectStatuses, fallback.status),
      deadline: dateValue(item.deadline, fallback.deadline),
      progress: progressValue(item.progress, fallback.progress),
      nextAction: stringValue(item.nextAction, fallback.nextAction).trim() || fallback.nextAction,
    },
    {
      goalId: optionalString(item.goalId),
      progressMode: optionalProgressMode(item.progressMode),
      budget: typeof item.budget === "number" && item.budget > 0 ? item.budget : undefined,
      createdAt: optionalIso(item.createdAt),
      completedAt: optionalIso(item.completedAt),
    },
  );
}

const inboxStatuses: InboxStatus[] = ["inbox", "someday", "waiting"];

/** D32: normalize embedded Key Results (max 5); returns undefined when none (keeps Simple mode). */
function normalizeKeyResults(value: unknown): KeyResult[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value
    .filter(isRecord)
    .slice(0, 5)
    .map((kr, index) =>
      withDefined<KeyResult>(
        {
          id: stringValue(kr.id).trim() || `kr_${index}`,
          name: stringValue(kr.name).trim() || `Kết quả ${index + 1}`,
          metric: stringValue(kr.metric).trim(),
          current: numberValue(kr.current, 0),
          target: positiveNumberValue(kr.target, 1),
        },
        {
          unit: optionalString(kr.unit),
          // P5-2a: auto-rollup link to a project
          linkedProjectId: optionalString(kr.linkedProjectId),
          auto: kr.auto === true ? true : undefined,
        },
      ),
    );
  return items.length ? items : undefined;
}

function normalizeGoal(item: Record<string, unknown>, fallback: Goal): Goal {
  return withDefined<Goal>(
    {
      id: stringValue(item.id, fallback.id),
      title: stringValue(item.title, fallback.title).trim() || fallback.title,
      horizon: enumValue(item.horizon, ["quarter", "year", "life"], fallback.horizon),
      metric: stringValue(item.metric, fallback.metric).trim() || fallback.metric,
      current: goalCurrentValue(item.current, fallback.current),
      target: positiveNumberValue(item.target, fallback.target),
      deadline: dateValue(item.deadline, fallback.deadline),
      status: enumValue(item.status, projectStatuses, fallback.status),
    },
    {
      objective: optionalString(item.objective),
      unit: optionalString(item.unit),
      progressMode: optionalProgressMode(item.progressMode),
      createdAt: optionalIso(item.createdAt),
      completedAt: optionalIso(item.completedAt),
      keyResults: normalizeKeyResults(item.keyResults),
    },
  );
}

function normalizeHabit(item: Record<string, unknown>, fallback: Habit): Habit {
  return withDefined<Habit>(
    {
      id: stringValue(item.id, fallback.id),
      name: stringValue(item.name, fallback.name).trim() || fallback.name,
      targetPerWeek: habitTargetPerWeekValue(item.targetPerWeek, fallback.targetPerWeek),
      completions: Array.isArray(item.completions)
        ? item.completions.filter((completion): completion is string => /^\d{4}-\d{2}-\d{2}$/.test(String(completion)))
        : fallback.completions,
      color: /^#[0-9a-fA-F]{6}$/.test(stringValue(item.color)) ? stringValue(item.color) : fallback.color,
    },
    {
      linkedGoalId: optionalString(item.linkedGoalId),
      createdAt: optionalIso(item.createdAt),
    },
  );
}

const noteCategories = ["project", "area", "resource", "archive"] as const;

function normalizeNote(item: Record<string, unknown>, fallback: Note): Note {
  return withDefined<Note>(
    {
      id: stringValue(item.id, fallback.id),
      title: stringValue(item.title, fallback.title).trim() || fallback.title,
      tags: Array.isArray(item.tags) ? item.tags.map((tag) => String(tag).trim()).filter(Boolean) : fallback.tags,
      body: stringValue(item.body, fallback.body),
      pinned: typeof item.pinned === "boolean" ? item.pinned : fallback.pinned,
      attachments: normalizeAttachments(item.attachments, fallback.attachments),
      updatedAt: isoValue(item.updatedAt, fallback.updatedAt),
    },
    {
      createdAt: optionalIso(item.createdAt),
      category: (noteCategories as readonly string[]).includes(stringValue(item.category)) ? stringValue(item.category) : undefined,
      linkedProjectId: optionalString(item.linkedProjectId),
      linkedGoalId: optionalString(item.linkedGoalId),
    },
  );
}

function normalizeTransaction(item: Record<string, unknown>, fallback: Transaction): Transaction {
  return withDefined<Transaction>(
    {
      id: stringValue(item.id, fallback.id),
      date: dateValue(item.date, fallback.date),
      type: enumValue(item.type, transactionTypes, fallback.type),
      category: stringValue(item.category, fallback.category).trim() || fallback.category,
      amount: nonNegativeNumberValue(item.amount, fallback.amount),
      note: stringValue(item.note, fallback.note),
    },
    {
      accountId: optionalString(item.accountId),
      toAccountId: optionalString(item.toAccountId),
      // D45: cross-currency transfer — số nhận về ở ví đích
      toAmount: typeof item.toAmount === "number" && item.toAmount > 0 ? item.toAmount : undefined,
      // P3-3: optional project link (cost rollup)
      projectId: optionalString(item.projectId),
      createdAt: optionalIso(item.createdAt),
      receipt: normalizeReceipt(item.receipt),
    },
  );
}

function normalizeAccount(item: Record<string, unknown>, fallback: Account): Account {
  return withDefined<Account>(
    {
      id: stringValue(item.id, fallback.id),
      name: stringValue(item.name, fallback.name).trim() || fallback.name,
      type: accountTypeValue(item.type, fallback.type),
      openingBalance: numberValue(item.openingBalance, fallback.openingBalance),
      currency: stringValue(item.currency, fallback.currency).trim() || fallback.currency,
    },
    { createdAt: optionalIso(item.createdAt) },
  );
}

function normalizeDailyPlan(item: Record<string, unknown>): DailyPlan {
  const date = dateValue(item.date, todayIso());
  return withDefined<DailyPlan>(
    {
      id: stringValue(item.id).trim() || date,
      date,
      mitTaskIds: idStringArray(item.mitTaskIds).slice(0, 3),
      createdAt: isoValue(item.createdAt, new Date().toISOString()),
    },
    {
      locked: typeof item.locked === "boolean" ? item.locked : undefined,
      replanReason: optionalString(item.replanReason),
    },
  );
}

function normalizeWeeklyReview(item: Record<string, unknown>): WeeklyReview {
  const scope: WeeklyReview["scope"] = item.scope === "month" ? "month" : "week";
  return withDefined<WeeklyReview>(
    {
      id: stringValue(item.id).trim() || `${scope}_${todayIso()}`,
      scope,
      date: dateValue(item.date, todayIso()),
      reflection: stringValue(item.reflection),
      createdAt: isoValue(item.createdAt, new Date().toISOString()),
    },
    {
      wins: optionalString(item.wins),
      blockers: optionalString(item.blockers),
      inboxCleared: typeof item.inboxCleared === "boolean" ? item.inboxCleared : undefined,
    },
  );
}

/** D36: normalize a ProjectTemplate from persisted / remote data; sanitizes all sub-task fields. */
export function normalizeProjectTemplate(item: Record<string, unknown>): ProjectTemplate {
  const rawTasks = Array.isArray(item.tasks) ? item.tasks : [];
  const tasks = rawTasks.filter(isRecord).map((t) => ({
    title: stringValue(t.title).trim() || "Việc chưa đặt tên",
    priority: enumValue(t.priority, priorities, "medium" as Priority),
    estimateMinutes: Math.max(0, Math.round(Number(t.estimateMinutes) || 0)),
    offsetDays: Math.max(0, Math.round(Number(t.offsetDays) || 0)),
  }));

  return withDefined<ProjectTemplate>(
    {
      id: stringValue(item.id).trim() || `template_${Date.now()}`,
      name: stringValue(item.name).trim() || "Mẫu chưa đặt tên",
      area: stringValue(item.area).trim() || "Chung",
      tasks,
    },
    { createdAt: optionalIso(item.createdAt) },
  );
}

/** D38: normalize a CurrencyRate from persisted/remote data.
 *  Returns null for unusable records (bad code) so callers can DROP them —
 *  coercing to a fallback code would mint bogus duplicate rates that sync back. */
export function normalizeCurrencyRate(item: Record<string, unknown>): CurrencyRate | null {
  const id = stringValue(item.id).trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(id)) {
    return null;
  }
  const rateToVnd = positiveNumberValue(item.rateToVnd, 1);
  return withDefined<CurrencyRate>(
    {
      id,
      rateToVnd,
      updatedAt: isoValue(item.updatedAt, new Date().toISOString()),
    },
    { createdAt: optionalIso(item.createdAt) },
  );
}

// D43: managed finance category — junk rows (no id/name) bị loại khi migrate.
export function normalizeFinanceCategory(item: Record<string, unknown>): FinanceCategory | null {
  const id = stringValue(item.id).trim();
  const name = stringValue(item.name).trim();
  if (!id || !name) {
    return null;
  }
  return withDefined<FinanceCategory>(
    {
      id,
      name,
      kind: item.kind === "income" ? "income" : "expense",
    },
    {
      archived: item.archived === true ? true : undefined,
      icon: typeof item.icon === "string" && item.icon.trim() ? item.icon.trim() : undefined,
      color: typeof item.color === "string" && item.color.trim() ? item.color.trim() : undefined,
      createdAt: optionalIso(item.createdAt),
    },
  );
}

function normalizeDailyReview(item: Record<string, unknown>): DailyReview {
  const date = dateValue(item.date, todayIso());
  return withDefined<DailyReview>(
    {
      id: stringValue(item.id).trim() || date,
      date,
      doneTaskIds: idStringArray(item.doneTaskIds),
      deferredTaskIds: idStringArray(item.deferredTaskIds),
      reflection: stringValue(item.reflection),
      createdAt: isoValue(item.createdAt, new Date().toISOString()),
    },
    { energy: optionalNumber(item.energy) === undefined ? undefined : Math.max(1, Math.min(5, Math.round(Number(item.energy)))) },
  );
}

function normalizeBudget(item: Record<string, unknown>, fallback: Budget): Budget {
  return withDefined<Budget>(
    {
      id: stringValue(item.id, fallback.id),
      category: stringValue(item.category, fallback.category).trim() || fallback.category,
      monthlyLimit: positiveNumberValue(item.monthlyLimit, fallback.monthlyLimit),
    },
    {
      rollover: typeof item.rollover === "boolean" ? item.rollover : undefined,
      createdAt: optionalIso(item.createdAt),
    },
  );
}

function normalizeInboxItem(item: Record<string, unknown>, fallback: InboxItem): InboxItem {
  return withDefined<InboxItem>(
    {
      id: stringValue(item.id, fallback.id),
      text: stringValue(item.text, fallback.text).trim() || fallback.text,
      hint: optionalString(item.hint),
      createdAt: isoValue(item.createdAt, fallback.createdAt),
    },
    {
      // absent/invalid status stays undefined → treated as "inbox" by derive (back-compat)
      status: inboxStatuses.includes(stringValue(item.status) as InboxStatus)
        ? (stringValue(item.status) as InboxStatus)
        : undefined,
      waitingFor: optionalString(item.waitingFor),
      waitingUntil: optionalDate(item.waitingUntil),
    },
  );
}

function normalizeCalendarEvent(item: Record<string, unknown>, fallback: CalendarEvent): CalendarEvent {
  const color = stringValue(item.color);
  return withDefined<CalendarEvent>(
    {
      id: stringValue(item.id, fallback.id),
      title: stringValue(item.title, fallback.title).trim() || fallback.title,
      date: dateValue(item.date, fallback.date),
      time: timeValue(item.time, fallback.time),
      color: /^#[0-9a-fA-F]{6}$/.test(color) ? color : fallback.color,
    },
    {
      endTime: optionalTime(item.endTime),
      durationMin: optionalNumber(item.durationMin) === undefined ? undefined : Math.max(0, Number(item.durationMin)),
      rrule: optionalString(item.rrule),
      exdates: dateStringArray(item.exdates).length ? dateStringArray(item.exdates) : undefined,
      linkedTaskId: optionalString(item.linkedTaskId),
      reminders: Array.isArray(item.reminders)
        ? (item.reminders.map((m) => Number(m)).filter((m) => Number.isFinite(m) && m >= 0) as number[])
        : undefined,
      createdAt: optionalIso(item.createdAt),
    },
  );
}

function normalizeRecurringRule(item: Record<string, unknown>, fallback: RecurringRule): RecurringRule {
  return withDefined<RecurringRule>(
    {
      id: stringValue(item.id, fallback.id),
      type: enumValue(item.type, transactionTypes, fallback.type),
      category: stringValue(item.category, fallback.category).trim() || fallback.category,
      amount: positiveNumberValue(item.amount, fallback.amount),
      dayOfMonth: Math.round(Math.max(1, Math.min(28, nonNegativeNumberValue(item.dayOfMonth, fallback.dayOfMonth)))),
      note: optionalString(item.note),
      startDate: dateValue(item.startDate, fallback.startDate),
    },
    {
      accountId: optionalString(item.accountId),
      createdAt: optionalIso(item.createdAt),
    },
  );
}

function normalizeDirtyItem(item: Record<string, unknown>): DirtyItem | null {
  if (!collectionNames.includes(item.collection as LifeOsCollectionName) || typeof item.id !== "string") {
    return null;
  }
  return {
    collection: item.collection as LifeOsCollectionName,
    id: item.id,
    changedAt: isoValue(item.changedAt, new Date().toISOString()),
  };
}

function normalizeDeletedItem(item: Record<string, unknown>): DeletedItem | null {
  if (!collectionNames.includes(item.collection as LifeOsCollectionName) || typeof item.id !== "string") {
    return null;
  }
  return {
    collection: item.collection as LifeOsCollectionName,
    id: item.id,
    deletedAt: isoValue(item.deletedAt, new Date().toISOString()),
  };
}

// ── Neutral per-item fallbacks (F10) ─────────────────────────────────────────
// Fallbacks for broken persisted records must be NEUTRAL: falling back to the
// createDefaultState() demo seed would fabricate sample content ("Xem lại màn
// Hôm nay…") inside real user data. Broken fields collapse to "Không tên" +
// today instead; broken ids get a fresh unique id.

function neutralTask(): Task {
  return { id: makeId("task"), title: "Không tên", dueDate: todayIso(), status: "todo", priority: "medium", estimateMinutes: 30, subtasks: [] };
}

function neutralProject(): Project {
  return { id: makeId("project"), name: "Không tên", area: "Chung", status: "active", deadline: todayIso(), progress: 0, nextAction: "" };
}

function neutralGoal(): Goal {
  return { id: makeId("goal"), title: "Không tên", horizon: "quarter", metric: "", current: 0, target: 1, deadline: todayIso(), status: "active" };
}

function neutralHabit(): Habit {
  return { id: makeId("habit"), name: "Không tên", targetPerWeek: 5, completions: [], color: "#3b82f6" };
}

function neutralNote(): Note {
  return { id: makeId("note"), title: "Không tên", tags: [], body: "", pinned: false, attachments: [], updatedAt: new Date().toISOString() };
}

function neutralTransaction(): Transaction {
  return { id: makeId("txn"), date: todayIso(), type: "expense", category: "Khác", amount: 0, note: "" };
}

function neutralBudget(): Budget {
  return { id: makeId("budget"), category: "Khác", monthlyLimit: 1 };
}

function neutralInboxItem(): InboxItem {
  return { id: makeId("inbox"), text: "Không tên", createdAt: new Date().toISOString() };
}

function neutralEvent(): CalendarEvent {
  return { id: makeId("event"), title: "Không tên", date: todayIso(), time: "09:00", color: "#2563eb" };
}

function neutralRecurringRule(): RecurringRule {
  return { id: makeId("rec"), type: "expense", category: "Khác", amount: 1, dayOfMonth: 1, startDate: todayIso() };
}

function neutralAccount(): Account {
  return { id: makeId("account"), name: "Ví không tên", type: "cash", openingBalance: 0, currency: "VND" };
}

export function migrateState(state: Partial<LifeOsState>): LifeOsState {
  const source = isRecord(state) ? state : {};

  const migrated: LifeOsState = {
    // F10: missing/invalid collections become [] and broken items fall back to
    // neutral values — never the demo seed (no fabricated sample data).
    tasks: normalizeRecords(source.tasks, []).map((item) => normalizeTask(item, neutralTask())),
    projects: normalizeRecords(source.projects, []).map((item) => normalizeProject(item, neutralProject())),
    goals: normalizeRecords(source.goals, []).map((item) => normalizeGoal(item, neutralGoal())),
    habits: normalizeRecords(source.habits, []).map((item) => normalizeHabit(item, neutralHabit())),
    notes: normalizeRecords(source.notes, []).map((item) => normalizeNote(item, neutralNote())),
    transactions: normalizeRecords(source.transactions, []).map((item) => normalizeTransaction(item, neutralTransaction())),
    budgets: normalizeRecords(source.budgets, []).map((item) => normalizeBudget(item, neutralBudget())),
    inbox: normalizeRecords(source.inbox, []).map((item) => normalizeInboxItem(item, neutralInboxItem())),
    events: normalizeRecords(source.events, []).map((item) => normalizeCalendarEvent(item, neutralEvent())),
    recurring: normalizeRecords(source.recurring, []).map((item) => normalizeRecurringRule(item, neutralRecurringRule())),
    // accounts fallback = [] (do NOT fabricate a wallet for migrated state; the
    // default wallet only ships in createDefaultState/createEmptyState).
    accounts: normalizeRecords(source.accounts, []).map((item) => normalizeAccount(item, neutralAccount())),
    dailyPlans: normalizeRecords(source.dailyPlans, []).map(normalizeDailyPlan),
    dailyReviews: normalizeRecords(source.dailyReviews, []).map(normalizeDailyReview),
    weeklyReviews: normalizeRecords(source.weeklyReviews, []).map(normalizeWeeklyReview),
    templates: normalizeRecords(source.templates, []).map(normalizeProjectTemplate),
    currencyRates: normalizeRecords(source.currencyRates, [])
      .map(normalizeCurrencyRate)
      .filter((rate): rate is CurrencyRate => rate !== null),
    financeCategories: normalizeRecords(source.financeCategories, [])
      .map(normalizeFinanceCategory)
      .filter((c): c is FinanceCategory => c !== null),
    dirtyItems: normalizeRecords(source.dirtyItems, []).map(normalizeDirtyItem).filter((item): item is DirtyItem => Boolean(item)),
    deletedItems: normalizeRecords(source.deletedItems, [])
      .map(normalizeDeletedItem)
      .filter((item): item is DeletedItem => Boolean(item)),
    updatedAt: isoValue(source.updatedAt, new Date().toISOString()),
  };

  const projectIds = new Set(migrated.projects.map((project) => project.id));
  const goalIds = new Set(migrated.goals.map((goal) => goal.id));

  return {
    ...migrated,
    tasks: migrated.tasks.map((task) => ({
      ...task,
      projectId: task.projectId && projectIds.has(task.projectId) ? task.projectId : undefined,
      goalId: task.goalId && goalIds.has(task.goalId) ? task.goalId : undefined,
    })),
    // P3-3: prune Transaction → Project links that reference a removed project.
    transactions: migrated.transactions.map((txn) =>
      txn.projectId && !projectIds.has(txn.projectId) ? { ...txn, projectId: undefined } : txn,
    ),
    // D2: prune Project → Goal links that reference a removed goal.
    projects: migrated.projects.map((project) =>
      project.goalId && !goalIds.has(project.goalId) ? { ...project, goalId: undefined } : project,
    ),
  };
}

export function hasLocalState(storageKey = createLocalStateStorageKey()) {
  return readStorageItem(storageKey) !== null;
}

export function loadLocalState(storageKey = createLocalStateStorageKey()): LifeOsState {
  const raw = readStorageItem(storageKey);
  if (!raw) {
    return createDefaultState();
  }

  try {
    const parsed = JSON.parse(raw) as Partial<LifeOsState>;
    if (isIndexedStateReference(parsed)) {
      // Placeholder while the real snapshot hydrates from IndexedDB. Must be
      // EMPTY (not demo seed): a user with an indexed reference has real data,
      // and the epoch updatedAt keeps this placeholder from winning any merge.
      return { ...createEmptyState(), updatedAt: INDEXED_STATE_PLACEHOLDER_UPDATED_AT };
    }
    return migrateState(parsed);
  } catch {
    // Corrupt persisted JSON is NOT a first run — return an empty state instead
    // of fabricating demo content inside an existing user's storage slot.
    return createEmptyState();
  }
}

export function loadLocalStateWithFallback(storageKey: string, fallbackStorageKey = createLocalStateStorageKey()): LifeOsState {
  if (hasLocalState(storageKey)) {
    return loadLocalState(storageKey);
  }

  if (storageKey !== fallbackStorageKey && hasLocalState(fallbackStorageKey)) {
    return loadLocalState(fallbackStorageKey);
  }

  return createDefaultState();
}

/** Persist state; returns false when the write failed (quota/private mode) so the UI can warn. */
export function saveLocalState(state: LifeOsState, storageKey = createLocalStateStorageKey()): boolean {
  return writeStorageItem(storageKey, JSON.stringify(state));
}
